import { useState, useEffect } from "react";
import { reportsApi, departmentsApi, type Contract, type Department } from "@/lib/api";
import { statusColors, statusLabels, type ContractStatus } from "@/lib/constants";

function formatZAR(value: string | null): string {
  if (!value) return "—";
  const n = parseFloat(value);
  return n.toLocaleString("en-ZA", { style: "currency", currency: "ZAR", minimumFractionDigits: 0 });
}

function downloadCSV(filename: string, headers: string[], rows: string[][]) {
  const bom = "﻿";
  const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const csv = bom + [headers.map(escape).join(","), ...rows.map((r) => r.map(escape).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

interface FilterState {
  dateFrom: string;
  dateTo: string;
  department: string;
  status: ContractStatus | "";
}

export default function ExpiryReport() {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewed, setPreviewed] = useState(false);
  const [filters, setFilters] = useState<FilterState>({ dateFrom: "", dateTo: "", department: "", status: "" });

  useEffect(() => {
    departmentsApi.list(1).then((res) => setDepartments(res.data.data)).catch(() => {});
  }, []);

  const updateFilter = (key: keyof FilterState, value: string) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const fetchContracts = async () => {
    setLoading(true);
    setPreviewed(true);
    try {
      const res = await reportsApi.allContracts({
        status: filters.status || undefined,
        department: filters.department || undefined,
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
      });
      setContracts(res.data);
    } catch {
      setContracts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const headers = ["Contract #", "Supplier", "Department", "Category", "Value (ZAR)", "Start Date", "End Date", "Status", "Responsible Officer"];
    const rows = contracts.map((c) => [
      c.contract_number, c.vendor_name, c.department,
      c.contract_type ?? "", c.contract_value ?? "",
      c.start_date, c.end_date,
      statusLabels[c.status as ContractStatus] ?? c.status,
      c.responsible_officer ?? "",
    ]);
    downloadCSV(`Contract_Expiry_Report_${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  };

  const hasFilters = filters.dateFrom || filters.dateTo || filters.department || filters.status;

  return (
    <div className="bg-white rounded-lg border border-background-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-background-100 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center shrink-0">
          <i className="ri-timer-flash-line text-accent-500"></i>
        </div>
        <div>
          <h2 className="text-foreground-900 text-sm font-semibold">Contract Expiry Report</h2>
          <p className="text-foreground-400 text-xs">Filter contracts by expiry date range and export to CSV</p>
        </div>
        {previewed && (
          <span className="ml-auto text-xs text-foreground-500 bg-background-100 px-2.5 py-1 rounded-full whitespace-nowrap">
            {contracts.length} contract{contracts.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      <div className="p-5">
        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div>
            <label className="block text-foreground-500 text-[11px] font-medium mb-1.5">Expiry From</label>
            <input type="date" value={filters.dateFrom} onChange={(e) => updateFilter("dateFrom", e.target.value)}
              className="w-full h-9 px-3 border border-background-300 rounded-md text-sm text-foreground-700 bg-white focus:outline-none focus:border-accent-300 focus:ring-1 focus:ring-accent-200" />
          </div>
          <div>
            <label className="block text-foreground-500 text-[11px] font-medium mb-1.5">Expiry To</label>
            <input type="date" value={filters.dateTo} onChange={(e) => updateFilter("dateTo", e.target.value)}
              className="w-full h-9 px-3 border border-background-300 rounded-md text-sm text-foreground-700 bg-white focus:outline-none focus:border-accent-300 focus:ring-1 focus:ring-accent-200" />
          </div>
          <div>
            <label className="block text-foreground-500 text-[11px] font-medium mb-1.5">Department</label>
            <select value={filters.department} onChange={(e) => updateFilter("department", e.target.value)}
              className="w-full h-9 px-3 border border-background-300 rounded-md text-sm text-foreground-700 bg-white cursor-pointer focus:outline-none focus:border-accent-300 focus:ring-1 focus:ring-accent-200">
              <option value="">All Departments</option>
              {departments.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-foreground-500 text-[11px] font-medium mb-1.5">Status</label>
            <select value={filters.status} onChange={(e) => updateFilter("status", e.target.value as ContractStatus | "")}
              className="w-full h-9 px-3 border border-background-300 rounded-md text-sm text-foreground-700 bg-white cursor-pointer focus:outline-none focus:border-accent-300 focus:ring-1 focus:ring-accent-200">
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="pending_renewal">Decision Pending</option>
              <option value="expired">Lapsed</option>
              <option value="terminated">Terminated</option>
            </select>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <button onClick={fetchContracts} disabled={loading}
            className="h-9 px-4 bg-background-100 text-foreground-700 text-sm font-medium rounded-md hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5 disabled:opacity-60">
            {loading ? <i className="ri-loader-4-line animate-spin text-sm"></i> : <i className="ri-search-line text-sm"></i>}
            Preview Results
          </button>
          <button onClick={handleExport} disabled={contracts.length === 0}
            className="h-9 px-4 bg-accent-500 text-white text-sm font-medium rounded-md hover:bg-accent-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5">
            <i className="ri-download-2-line text-sm"></i>
            Export {contracts.length > 0 ? `${contracts.length} Record${contracts.length !== 1 ? "s" : ""}` : ""} (CSV)
          </button>
        </div>

        {/* Preview Table */}
        {previewed && (
          <div className="mt-4 border border-background-200 rounded-lg overflow-hidden">
            {loading ? (
              <div className="py-10 text-center text-foreground-400 text-sm">
                <i className="ri-loader-4-line animate-spin mr-2"></i>Loading...
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-50 border-b border-background-200">
                      {["Contract #", "Supplier", "Department", "Category", "Value", "End Date", "Status", "Officer"].map((h) => (
                        <th key={h} className="text-left px-3 py-2.5 text-foreground-500 text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {contracts.map((c, i) => {
                      const sc = statusColors[c.status as ContractStatus] ?? statusColors.active;
                      return (
                        <tr key={c.id} className={`border-b border-background-100 hover:bg-background-50/50 transition-colors ${i === contracts.length - 1 ? "border-b-0" : ""}`}>
                          <td className="px-3 py-2.5 text-foreground-800 font-medium text-xs whitespace-nowrap">{c.contract_number}</td>
                          <td className="px-3 py-2.5 text-foreground-700 text-xs whitespace-nowrap">{c.vendor_name}</td>
                          <td className="px-3 py-2.5 text-foreground-500 text-xs whitespace-nowrap">{c.department}</td>
                          <td className="px-3 py-2.5 text-foreground-500 text-xs whitespace-nowrap">{c.contract_type ?? "—"}</td>
                          <td className="px-3 py-2.5 text-foreground-700 text-xs whitespace-nowrap font-medium">{formatZAR(c.contract_value)}</td>
                          <td className="px-3 py-2.5 text-foreground-700 text-xs whitespace-nowrap">{c.end_date}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${sc.bg} ${sc.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}></span>
                              {statusLabels[c.status as ContractStatus] ?? c.status}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-foreground-700 text-xs whitespace-nowrap">{c.responsible_officer ?? "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {contracts.length === 0 && (
                  <div className="py-10 text-center text-foreground-400 text-sm">No contracts match the selected filters</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
